import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Skeleton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";
import { auth } from "../../config/firebaseConfig";

const TotalItemsReturned = ({ isTabView }) => {
  const [returnedItems, setReturnedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReturnedItems();
  }, []);

  const fetchReturnedItems = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      
      // Fetch claims that are approved/completed
      const claimsResponse = await fetch(getApiUrl("api/claims"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      
      if (!claimsResponse.ok) throw new Error("Failed to fetch claims");
      
      const claimsData = await claimsResponse.json();
      
      // Fetch items to get additional details
      const itemsResponse = await fetch(getApiUrl("api/items"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      
      const itemsData = await itemsResponse.json();
      
      // Filter approved/completed claims and join with item data
      const formattedData = (claimsData.claims || [])
        .filter(claim => claim.status === 'approved' || claim.status === 'completed')
        .map(claim => {
          const item = itemsData.items?.find(i => i.id === claim.found_item_id);
          return {
            itemName: item?.item_name || claim.item_description || "Unknown",
            category: item?.item_type || "N/A",
            dateFound: item?.date_found 
              ? new Date(item.date_found).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
              : "N/A",
            dateReturned: claim.updated_at 
              ? new Date(claim.updated_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
              : new Date(claim.claim_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
            ownerName: claim.full_name || "Unknown",
          };
        });
      
      setReturnedItems(formattedData);
    } catch (error) {
      console.error("Error fetching returned items:", error);
    } finally {
      setLoading(false);
    }
  };
  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  if (isTabView) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, bgcolor: "#fbffe8", borderRadius: "15px", border: "2px solid #516d4f" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", borderBottom: "1px solid rgba(81, 109, 79, 1)", fontWeight: 500, color: "#516d4f", fontSize: "1.5rem" }}>Item Name</TableCell>
                <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", borderBottom: "1px solid rgba(81, 109, 79, 1)", fontWeight: 500, color: "#516d4f", fontSize: "1.5rem" }}>Category</TableCell>
                <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", borderBottom: "1px solid rgba(81, 109, 79, 1)", fontWeight: 500, color: "#516d4f", fontSize: "1.5rem" }}>Date Found</TableCell>
                <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", borderBottom: "1px solid rgba(81, 109, 79, 1)", fontWeight: 500, color: "#516d4f", fontSize: "1.5rem" }}>Date Returned</TableCell>
                <TableCell sx={{ borderBottom: "1px solid rgba(81, 109, 79, 1)", fontWeight: 500, color: "#516d4f", fontSize: "1.5rem" }}>Owner Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)" }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)" }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)" }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)" }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                    <TableCell><Skeleton variant="text" width="80%" height={30} /></TableCell>
                  </TableRow>
                ))
              ) : (
                returnedItems.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", color: "#516d4f", fontSize: "1.5rem" }}>{item.itemName}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", color: "#516d4f", fontSize: "1.5rem" }}>{item.category}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", color: "#516d4f", fontSize: "1.5rem" }}>{item.dateFound}</TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", color: "#516d4f", fontSize: "1.5rem" }}>{item.dateReturned}</TableCell>
                    <TableCell sx={{ color: "#516d4f", fontSize: "1.5rem" }}>{item.ownerName}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ bgcolor: "#516d4f", borderRadius: "25px", p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
          <Typography variant="h4" fontWeight="600" color="#f6ffc9" sx={{ ml: 2 }}>
            Total Items Returned
          </Typography>
          <Typography variant="h3" fontWeight="600" color="#f6ffc9" sx={{ mr: 2 }}>
            {returnedItems.length}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Full page view (original)
  return (
    <Box
      sx={{
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        bgcolor: "#fbffe8",
        display: "flex",
        justifyContent: "center",
        width: "100%",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#fbffe8",
          width: "100%",
          maxWidth: 1920,
          position: "relative",
          height: "100vh",
          p: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton sx={{ mr: 2 }}>
            <MenuIcon sx={{ fontSize: 36, color: "#516d4f" }} />
          </IconButton>
          <Typography variant="h5" fontWeight="bold" color="#516d4f">
            UFind
          </Typography>
        </Box>

        <Box sx={{ px: 2, py: 1 }}>
          {/* Title and Search Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="#516d4f">
              Total Items Returned
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Autocomplete
                options={[]}
                sx={{
                  width: 320,
                  "& .MuiInputBase-root": {
                    bgcolor: "rgba(81, 109, 79, 0.6)",
                    borderRadius: "30px",
                    color: "#f6ffc9",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <SearchIcon sx={{ color: "#f6ffc9", ml: 1, mr: 1 }} />
                      ),
                    }}
                  />
                )}
                popupIcon={
                  <ArrowDropDownIcon sx={{ color: "#f6ffc9", fontSize: 40 }} />
                }
              />
            </Box>
          </Box>

          {/* Main Content */}
          <Paper
            elevation={4}
            sx={{
              bgcolor: "#e1ecc8",
              borderRadius: "20px",
              p: 2,
              mb: 2,
              height: "calc(100vh - 200px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Table */}
            <TableContainer
              component={Paper}
              sx={{
                bgcolor: "#fbffe8",
                borderRadius: "20px",
                border: "2px solid #516d4f",
                flexGrow: 1,
                overflow: "auto",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                        borderBottom: "1px solid rgba(81, 109, 79, 1)",
                        fontWeight: 500,
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      Item Name
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                        borderBottom: "1px solid rgba(81, 109, 79, 1)",
                        fontWeight: 500,
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                        borderBottom: "1px solid rgba(81, 109, 79, 1)",
                        fontWeight: 500,
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      Date Found
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                        borderBottom: "1px solid rgba(81, 109, 79, 1)",
                        fontWeight: 500,
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      Date Returned
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid rgba(81, 109, 79, 1)",
                        fontWeight: 500,
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      Owner Name
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnedItems.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell
                        sx={{
                          borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                          color: "#516d4f",
                          fontSize: "1.5rem",
                        }}
                      >
                        {item.itemName}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                          color: "#516d4f",
                          fontSize: "1.5rem",
                        }}
                      >
                        {item.category}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                          color: "#516d4f",
                          fontSize: "1.5rem",
                        }}
                      >
                        {item.dateFound}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                          color: "#516d4f",
                          fontSize: "1.5rem",
                        }}
                      >
                        {item.dateReturned}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#516d4f",
                          fontSize: "1.5rem",
                        }}
                      >
                        {item.ownerName}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total Items Footer */}
            <Box
              sx={{
                bgcolor: "#516d4f",
                borderRadius: "25px",
                p: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 3,
              }}
            >
              <Typography
                variant="h4"
                fontWeight="600"
                color="#f6ffc9"
                sx={{ ml: 2 }}
              >
                Total Items Returned
              </Typography>
              <Typography
                variant="h3"
                fontWeight="600"
                color="#f6ffc9"
                sx={{ mr: 2 }}
              >
                {returnedItems.length}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default TotalItemsReturned